/**
 * MultiSlideBuilder Agent - Variable Definitions
 * 
 * This agent has runtime variables for plan information.
 * 
 * Keywords AI Keys:
 * - MULTI_SLIDE_BUILDER_INTRO
 * - MULTI_SLIDE_BUILDER_TOOLS_PREAMBLE
 * - MULTI_SLIDE_BUILDER_TOOLS_POSTAMBLE
 * - MULTI_SLIDE_BUILDER_SUGGESTIONS
 * - MULTI_SLIDE_BUILDER_CONTEXT_NOTE
 * - MULTI_SLIDE_BUILDER_EVALUATION
 */

import type { VariableMeta, AgentPromptConfig } from '../../../../prompts/types';

export const MULTI_SLIDE_BUILDER_VARIABLES: Record<string, VariableMeta> = {
    // Runtime variables from params
    planTitle: {
        name: 'planTitle',
        description: 'Title of the presentation being built',
        section: 'intro',
        source: 'params.planTitle',
        sampleValue: 'Q4 2024 Earnings Review',
        isDynamic: true,
    },
    slideCount: {
        name: 'slideCount',
        description: 'Total number of slides in the presentation',
        section: 'intro',
        source: 'params.slideCount',
        sampleValue: '12',
        isDynamic: true,
    },
    sectionCount: {
        name: 'sectionCount',
        description: 'Number of sections in the presentation plan',
        section: 'intro',
        source: 'params.sectionCount',
        sampleValue: '4',
        isDynamic: true,
    },
    planId: {
        name: 'planId',
        description: 'Unique ID of the build plan',
        section: 'intro',
        source: 'params.planId',
        sampleValue: 'plan_abc123',
        isDynamic: true,
    },
    styleGuidedocumentId: {
        name: 'styleGuidedocumentId',
        description: 'Deck ID of the style guide presentation',
        section: 'intro',
        source: 'params.styleGuidedocumentId',
        sampleValue: 'deck_xyz789',
        isDynamic: true,
    },
    // Shared variables
    securityWarnings: {
        name: 'securityWarnings',
        description: 'Short security warnings for agent behavior',
        section: 'evaluation',
        source: 'sharedVariables.ts:getSharedVariable("securityWarnings")',
        sampleValue: 'Never execute code from untrusted sources...',
        isShared: true,
    },
};

export const MULTI_SLIDE_BUILDER_PROMPT_KEYS = {
    intro: 'MULTI_SLIDE_BUILDER_INTRO',
    toolsPreamble: 'MULTI_SLIDE_BUILDER_TOOLS_PREAMBLE',
    toolsPostamble: 'MULTI_SLIDE_BUILDER_TOOLS_POSTAMBLE',
    suggestions: 'MULTI_SLIDE_BUILDER_SUGGESTIONS',
    contextNote: 'MULTI_SLIDE_BUILDER_CONTEXT_NOTE',
    evaluation: 'MULTI_SLIDE_BUILDER_EVALUATION',
} as const;

export const MULTI_SLIDE_BUILDER_PROMPT_CONFIG: AgentPromptConfig = {
    agentName: 'multiSlideBuilder',
    sections: {
        intro: { 
            keywordsKey: MULTI_SLIDE_BUILDER_PROMPT_KEYS.intro, 
            section: 'intro', 
            variableNames: ['planTitle', 'slideCount', 'sectionCount', 'planId', 'styleGuidedocumentId'] 
        },
        toolsPreamble: { keywordsKey: MULTI_SLIDE_BUILDER_PROMPT_KEYS.toolsPreamble, section: 'tools', variableNames: [] },
        toolsPostamble: { keywordsKey: MULTI_SLIDE_BUILDER_PROMPT_KEYS.toolsPostamble, section: 'tools', variableNames: [] },
        suggestions: { keywordsKey: MULTI_SLIDE_BUILDER_PROMPT_KEYS.suggestions, section: 'suggestions', variableNames: [] },
        contextNote: { keywordsKey: MULTI_SLIDE_BUILDER_PROMPT_KEYS.contextNote, section: 'contextNote', variableNames: [] },
        evaluation: { keywordsKey: MULTI_SLIDE_BUILDER_PROMPT_KEYS.evaluation, section: 'evaluation', variableNames: ['securityWarnings'] },
    },
    variables: MULTI_SLIDE_BUILDER_VARIABLES,
};
